import { defineComponent as _defineComponent } from 'vue'
import { unref as _unref, createVNode as _createVNode, createElementVNode as _createElementVNode, renderList as _renderList, Fragment as _Fragment, openBlock as _openBlock, createElementBlock as _createElementBlock, toDisplayString as _toDisplayString, normalizeClass as _normalizeClass, createTextVNode as _createTextVNode, withCtx as _withCtx } from "vue"

const _hoisted_1 = { class: "setting-dashboard-alert-page" }
const _hoisted_2 = { class: "setting-dashboard-alert-page__header" }
const _hoisted_3 = { class: "container__title" }
const _hoisted_4 = { class: "setting" }
const _hoisted_5 = { class: "input__title" }
const _hoisted_6 = { class: "button__container" }

import { i18n } from '@/i18n';
import SettingsPageTitle from '@/settingsModule/components/SettingsPageTitle.vue';
import { computed, nextTick, onBeforeUnmount, onMounted, ref } from 'vue';
import {
  DashboardAlertDirections,
  DashboardAlertSeverities,
  DashboardAlertSetting,
  DashboardAlertSettingsValueType,
  DashboardAlertSeverity,
  DashboardAlertDirection,
  DashboardAlertSettingsValueTypes,
  dashboardAlertSettingValidation,
  DEFAULT_DASHBOARD_ALERT_SETTING,
} from '../domains/entities/DashboardAlert';
import AlertInputConsecutive from '../components/alertInput/AlertInputConsecutive.vue';
import AlertInputSharp from '../components/alertInput/AlertInputSharp.vue';
import TextButton from '@/components/input/Btn/TextButton.vue';
import { DashboardAlertSettingFactory } from '../domains/factories/DashboardAlertSettingFactory';
import { diContainer } from '@/coreContainer/container';
import { waitLoading } from '@/corePagesModule/components/InfoModal/waitLoading';
import { showInfoModal } from '@/corePagesModule/components/InfoModal/InfoModalManager';
import ErrorMessage from '@/corePagesModule/components/ErrorMessage.vue';
import { useRouter } from 'vue-router';
import { simpleCopy } from '@/utils/deepCopy';


export default /*@__PURE__*/_defineComponent({
  __name: 'SettingDashboardAlertPage',
  setup(__props) {

const t = i18n.t;
const router = useRouter();

const text = {
  excess: {
    title: '過剰アラートの設定',
    critical: {
      consecutive: { key: 'dashboard:デフォルト:nヶ月連続でm%未満', option: { n: 2, m: 50 } },
      sharp: { key: 'dashboard:デフォルト:前月比n%以下かつ数量m以上', option: { n: 50, m: 100 } },
    },
    warning: {
      consecutive: { key: 'dashboard:デフォルト:nヶ月連続でm%未満', option: { n: 2, m: 80 } },
      sharp: { key: 'dashboard:デフォルト:前月比n%以下かつ数量m以上', option: { n: 75, m: 100 } },
    },
  },
  shortage: {
    title: '不足アラートの設定',
    critical: {
      consecutive: { key: 'dashboard:デフォルト:nヶ月連続でm%より高い', option: { n: 2, m: 150 } },
      sharp: { key: 'dashboard:デフォルト:前月比n%以上かつ数量m以上', option: { n: 200, m: 100 } },
    },
    warning: {
      consecutive: { key: 'dashboard:デフォルト:nヶ月連続でm%より高い', option: { n: 2, m: 120 } },
      sharp: { key: 'dashboard:デフォルト:前月比n%以上かつ数量m以上', option: { n: 125, m: 100 } },
    },
  },
};

const inputValues = ref<DashboardAlertSetting>(DashboardAlertSettingFactory.create());
const isEdited = ref(false);
const errorMessage = ref<DashboardAlertSetting>(DashboardAlertSettingFactory.create());
const bySeverityDirectionErrorMessage = computed(() =>
  Object.fromEntries(
    DashboardAlertSeverities.map((severity) => [
      severity,
      Object.fromEntries(
        DashboardAlertDirections.map((direction) => [
          direction,
          Object.values(errorMessage.value[severity][direction]).filter((s) => s !== '')[0],
        ])
      ),
    ])
  )
);
const isError = computed(() => Object.values(bySeverityDirectionErrorMessage.value).some((dv) => Object.values(dv).some((es) => es)));

/**
 * Stateをリセットする
 */
const resetState = () => {
  inputValues.value = DashboardAlertSettingFactory.create();
  errorMessage.value = DashboardAlertSettingFactory.create();
  isEdited.value = false;
};

/**
 * データフェッチ
 */
const fetch = async () => {
  const res = await waitLoading(diContainer.getDashboardAlertSettingUseCase.execute(), undefined, '');
  if (!res) {
    await showInfoModal({
      icon: 'error',
      modalTitle: t('取得に失敗しました'),
      modalDescription: t('情報の取得に失敗しました。リロードしてください。'),
      backButtonLabel: 'OK',
      isDisabledNextButton: true,
    });
    return;
  }
  switch (res.status.type) {
    case 'success':
      inputValues.value = res.status.data;
      return;
    default:
      await showInfoModal({
        icon: 'error',
        modalTitle: t('取得に失敗しました'),
        modalDescription: t('情報の取得に失敗しました。リロードしてください。'),
        backButtonLabel: 'OK',
        isDisabledNextButton: true,
      });
  }
};

/**
 * Inputハンドラ
 * @param e - Inputイベント
 * @param severity - 危険度
 * @param direction - 向き
 * @param valueType - 値種類
 */
const onInput = async (
  e: Event,
  severity: DashboardAlertSeverity,
  direction: DashboardAlertDirection,
  valueType: DashboardAlertSettingsValueType
) => {
  isEdited.value = true;
  const x = (e.target as HTMLInputElement).value;
  inputValues.value[severity][direction][valueType] = x;
  errorMessage.value[severity][direction][valueType] = dashboardAlertSettingValidation(x, valueType);
};

/**
 * 保存ハンドラ
 */
const onClickSave = async () => {
  for (const severity of DashboardAlertSeverities) {
    for (const direction of DashboardAlertDirections) {
      for (const valueType of DashboardAlertSettingsValueTypes) {
        errorMessage.value[severity][direction][valueType] = dashboardAlertSettingValidation(
          inputValues.value[severity][direction][valueType],
          valueType as DashboardAlertSettingsValueType
        );
      }
    }
  }
  await nextTick();
  if (isError.value) {
    return;
  }
  await save();
};

/**
 * リセットハンドラ
 */
const onReset = async () => {
  const confirmReset = await showInfoModal({
    icon: 'info',
    modalTitle: t('デフォルトの設定に戻してよろしいですか？（このまま保存されます）'),
    backButtonLabel: 'キャンセル',
    nextButtonLabel: 'OK',
    noDescription: true,
  });
  if (!confirmReset) return;
  inputValues.value = simpleCopy(DEFAULT_DASHBOARD_ALERT_SETTING);
  await save();
};

/**
 * 保存
 */
const save = async () => {
  const result = await waitLoading(diContainer.putDashboardAlertSettingUseCase.execute(inputValues.value));
  if (!result?.status.type || result.status.type === 'error') {
    showInfoModal({
      icon: 'error',
      colorStyle: 'error',
      modalTitle: t('保存に失敗しました'),
      modalDescription: t('時間をおいて再度お試しください。'),
      backButtonLabel: 'OK',
      isDisabledNextButton: true,
    });
    return;
  }
  const wantBackToAlertPage = await showInfoModal({
    modalTitle: t('保存しました'),
    modalDescription: t('ダッシュボードに戻りますか？'),
    backButtonLabel: t('キャンセル'),
    nextButtonLabel: t('ダッシュボードに戻る'),
  });
  if (wantBackToAlertPage) {
    router.push('/inventory-dashboard');
  } else {
    resetState();
    await fetch();
  }
};

onMounted(async () => {
  await fetch();
});

onBeforeUnmount(() => {
  resetState();
});

return (_ctx: any,_cache: any) => {
  return (_openBlock(), _createElementBlock("section", _hoisted_1, [
    _createElementVNode("header", _hoisted_2, [
      _createVNode(SettingsPageTitle, {
        label: _unref(t)('設定 > 発注・計算ロジック > ダッシュボードアラート設定')
      }, null, 8, ["label"])
    ]),
    (_openBlock(true), _createElementBlock(_Fragment, null, _renderList(_unref(DashboardAlertDirections), (direction) => {
      return (_openBlock(), _createElementBlock("div", {
        key: direction,
        class: "container"
      }, [
        _createElementVNode("div", _hoisted_3, _toDisplayString(_unref(t)(text[direction].title)), 1),
        _createElementVNode("div", _hoisted_4, [
          (_openBlock(true), _createElementBlock(_Fragment, null, _renderList(_unref(DashboardAlertSeverities), (severity) => {
            return (_openBlock(), _createElementBlock("div", {
              key: severity,
              class: "setting__input"
            }, [
              _createElementVNode("div", {
                class: _normalizeClass(`input__container input__container--${severity}`)
              }, [
                _createElementVNode("div", _hoisted_5, [
                  _createElementVNode("span", null, _toDisplayString(_unref(t)(`dashboard:${severity}`)), 1),
                  _createVNode(ErrorMessage, {
                    "is-error": !!bySeverityDirectionErrorMessage.value[severity][direction],
                    "error-message": bySeverityDirectionErrorMessage.value[severity][direction]
                  }, null, 8, ["is-error", "error-message"])
                ]),
                _createVNode(AlertInputConsecutive, {
                  direction: direction,
                  "consecutive-months": inputValues.value[severity][direction].consecutiveMonths,
                  "ratio-threshold": inputValues.value[severity][direction].ratioThreshold,
                  "default-value-string": _unref(t)(text[direction][severity].consecutive.key, text[direction][severity].consecutive.option),
                  "onUpdate:consecutiveMonths": (e) => onInput(e, severity, direction, 'consecutiveMonths'),
                  "onUpdate:ratioThreshold": (e) => onInput(e, severity, direction, 'ratioThreshold')
                }, null, 8, ["direction", "consecutive-months", "ratio-threshold", "default-value-string", "onUpdate:consecutiveMonths", "onUpdate:ratioThreshold"]),
                _createVNode(AlertInputSharp, {
                  severity: severity,
                  direction: direction,
                  "mom-change-rate-threshold": inputValues.value[severity][direction].momChangeRateThreshold,
                  "min-quantity-threshold": inputValues.value[severity][direction].minQuantityThreshold,
                  "default-value-string": _unref(t)(text[direction][severity].sharp.key, text[direction][severity].sharp.option),
                  "onUpdate:momChangeRateThreshold": (e) => onInput(e, severity, direction, 'momChangeRateThreshold'),
                  "onUpdate:minQuantityThreshold": (e) => onInput(e, severity, direction, 'minQuantityThreshold')
                }, null, 8, ["severity", "direction", "mom-change-rate-threshold", "min-quantity-threshold", "default-value-string", "onUpdate:momChangeRateThreshold", "onUpdate:minQuantityThreshold"])
              ], 2)
            ]))
          }), 128))
        ])
      ]))
    }), 128)),
    _createElementVNode("div", _hoisted_6, [
      _createVNode(TextButton, {
        disabled: !isEdited.value || isError.value,
        onClick: onClickSave
      }, {
        default: _withCtx(() => [
          _createTextVNode(_toDisplayString(_unref(t)('保存')), 1)
        ], undefined, true),
        _: 1
      }, 8, ["disabled"]),
      _createElementVNode("button", {
        class: "button--back",
        onClick: onReset
      }, _toDisplayString(_unref(t)('デフォルトに戻す')), 1)
    ])
  ]))
}
}

})